import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import {
    isLoginFlowActive,
    lastForcedRedirectAt,
    markForcedRedirect,
    shouldForceLogout,
    shouldVerifySession,
} from '@/libs/authGuard'
import { isPublicPath } from '@/libs/publicRoutes'
import { useAppStore } from '@/stores/app'

const API_BASE = '/api/admin-api/v1'

// Hard ceiling for the /me re-check. Without it a request that never settles
// (a hung connection, a proxy holding the socket) would leave the single-flight
// guard below latched forever, and no later 401 would ever be verified again.
const SESSION_CHECK_TIMEOUT_MS = 10_000

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    // Single-flight guard: a burst of 401s (several parallel requests, a poller
    // firing next to a page load) must trigger at most ONE /me re-check.
    let recovering: Promise<void> | null = null

    // sessionAlive re-checks the session with the cheapest authenticated endpoint,
    // deliberately through the raw window.fetch: going through the store's fetch
    // would run this very error handler again and recurse. A network/CORS failure
    // returns true — an unreachable server is not an expired session, and must
    // never sign the operator out.
    const sessionAlive = async (): Promise<boolean> => {
        const jwt = appStore.getJWT()
        if (!jwt) return false

        try {
            const res = await window.fetch(`${API_BASE}/me`, {
                headers: { Authorization: `Bearer ${jwt}` },
                signal:  AbortSignal.timeout(SESSION_CHECK_TIMEOUT_MS),
            })
            return res.status !== 401
        } catch {
            return true
        }
    }

    // recover implements the 401 policy from libs/authGuard: verify first, sign
    // out only for a session that is really gone, and never more than one forced
    // redirect per cooldown window.
    const recover = async (): Promise<void> => {
        const ctx = {
            loggedIn:      appStore.isLoggedIn(),
            publicPath:    isPublicPath(),
            loginInFlight: isLoginFlowActive(),
        }
        if (!shouldVerifySession(ctx)) return

        const alive = await sessionAlive()

        if (!shouldForceLogout({
            ...ctx,
            sessionAlive:   alive,
            lastRedirectAt: lastForcedRedirectAt(),
            now:            Date.now(),
        })) return

        // The session is confirmed dead: recover with a full navigation rather
        // than router.push(), which can silently no-op behind an open PrimeVue
        // dialog mask or while the router is mid-transition.
        markForcedRedirect()
        appStore.setLogout()
        window.location.href = '/login'
    }

    const fetch = createFetch({
        baseUrl: API_BASE,
        options: {
            beforeFetch({ options }) {
                const headers = new Headers(options.headers)
                headers.set('Authorization', `Bearer ${appStore.getJWT()}`)
                options.headers = headers
                return { options }
            },
            onFetchError(ctx) {
                // A 401 on a single request is NOT proof that the session ended
                // (a stale poller, a request that started before sign-in, a race
                // with the token being stored). Verify, then decide — the failing
                // request itself always just fails.
                if (ctx.response?.status === 401 && !recovering) {
                    recovering = recover().finally(() => { recovering = null })
                }
                return ctx
            },
        },
    })

    return { fetch }
})
